import { useState, useRef, forwardRef, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { hasMatch } from 'Utils/strings';
import MultiselectItem from './MultiselectItem';
import MultiselectFilter from './MultiselectFilter';
import ErrorMessage from 'Components/Atoms/ErrorMessage/ErrorMessage';
import { useTranslation } from 'react-i18next';
import FloatLabel from 'Components/Atoms/FloatLabel/FloatLabel';
import MultiselectTrigger from './MultiselectTrigger';
import Dropdown from 'Components/Atoms/Dropdown/Dropdown';
import DropdownContext from 'Components/Atoms/Dropdown/DropdownContext';

const Multiselect = forwardRef(function Multiselect(props, ref) {
    const {
        options = [],
        optionsSelected = [],
        actions = [],
        onChangeSelected,
        size: selectSize,
        className,
        label,
        renderOption,
        toggleAllText = '',
        enableSelectAll = false,
        disabled = false,
        floatLabel,
        enableClear = false,
        onClear,
        enableFilter = false,
        emptyValue,
        icon,
        error,
        highlight,
    } = props;
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const { t } = useTranslation('common');
    const [isHighlighted, setIsHighlighted] = useState(highlight || false);
    const [activeIndex, setActiveIndex] = useState(null);
    const listRef = useRef([]);

    const hasError = !!error;
    const status = isHighlighted ? 'highlight' : hasError ? 'error' : 'default';

    const handleFilter = (ev) => {
        setQuery(ev.target.value);
        setActiveIndex(null);
    };

    const selectedOptions = options.filter((opt) => optionsSelected.includes(opt.id));

    const handleToggle = (value) => {
        if (optionsSelected.includes(value)) {
            onChangeSelected(optionsSelected.filter((v) => v !== value));
        } else {
            onChangeSelected([...optionsSelected, value]);
        }
    };
    const emptyOption = emptyValue ? options.find((option) => option.id === emptyValue) : null;
    const optionsFormatted = options.filter((option) => option.id !== emptyValue);
    const optionsSelectedWithoutEmpty = optionsSelected.filter((id) => id !== emptyValue);
    const isAllSelected = optionsFormatted.every((option) => optionsSelected.includes(option.id));

    const optionsFiltered = query.trim()
        ? optionsFormatted.filter((option) => hasMatch(option.label, query))
        : optionsFormatted;

    const handleFloatingKeyDown = useCallback(
        (e) => {
            if ((e.key === 'Enter' || e.key === ' ') && activeIndex != null && optionsFiltered[activeIndex]) {
                e.preventDefault();
                handleToggle(optionsFiltered[activeIndex].id);
            }
        },
        [activeIndex, optionsFiltered],
    );

    return (
        <div className="relative min-w-0 group">
            <Dropdown
                open={isOpen}
                onOpenChange={(open) => {
                    setIsHighlighted(false);
                    setIsOpen(open);
                    if (!open) {
                        setActiveIndex(null);
                        setQuery('');
                    }
                }}
                listRef={listRef}
                activeIndex={activeIndex}
                onNavigate={setActiveIndex}
                virtual
                onFloatingKeyDown={handleFloatingKeyDown}
                trigger={
                    <>
                        <FloatLabel label={floatLabel} hasError={hasError} isOpen={isOpen} highlight={isHighlighted} />
                        <MultiselectTrigger
                            size={selectSize}
                            className={className}
                            label={label}
                            disabled={disabled}
                            enableClear={enableClear}
                            onClear={onClear}
                            icon={icon}
                            status={status}
                            isOpen={isOpen}
                            hasSelected={selectedOptions.length > 0}
                            ref={ref}
                        />
                    </>
                }
            >
                <MultiselectContent
                    enableFilter={enableFilter}
                    query={query}
                    onFilter={handleFilter}
                    optionsFiltered={optionsFiltered}
                    optionsSelected={optionsSelected}
                    handleToggle={handleToggle}
                    renderOption={renderOption}
                    activeIndex={activeIndex}
                    listRef={listRef}
                    enableSelectAll={enableSelectAll}
                    emptyOption={emptyOption}
                    isAllSelected={isAllSelected}
                    optionsSelectedWithoutEmpty={optionsSelectedWithoutEmpty}
                    onChangeSelected={onChangeSelected}
                    optionsFormatted={optionsFormatted}
                    toggleAllText={toggleAllText}
                    actions={actions}
                    setIsOpen={setIsOpen}
                    t={t}
                />
            </Dropdown>
            <ErrorMessage message={error} />
        </div>
    );
});

function MultiselectContent({
    enableFilter,
    query,
    onFilter,
    optionsFiltered,
    optionsSelected,
    handleToggle,
    renderOption,
    activeIndex,
    listRef,
    enableSelectAll,
    emptyOption,
    isAllSelected,
    optionsSelectedWithoutEmpty,
    onChangeSelected,
    optionsFormatted,
    toggleAllText,
    actions,
    setIsOpen,
    t,
}) {
    const { getItemProps } = useContext(DropdownContext);

    return (
        <>
            {enableFilter && <MultiselectFilter value={query} onChange={onFilter} />}
            <div className="p-1.5 min-h-12">
                {optionsFiltered.length === 0 && !!query && (
                    <div className="text-center text-gray-500 text-sm py-6">{t('no-results-found')}</div>
                )}

                {optionsFiltered.length === 0 && !query && (
                    <div className="text-center text-gray-500 text-sm py-6"> {t('no-options-available')}</div>
                )}

                {optionsFiltered.map((opt, idx) => (
                    <div
                        key={opt.id}
                        ref={(node) => {
                            listRef.current[idx] = node;
                        }}
                        tabIndex={activeIndex === idx ? 0 : -1}
                        className="outline-none"
                        {...getItemProps({
                            onClick() {
                                handleToggle(opt.id);
                            },
                            onKeyDown(e) {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleToggle(opt.id);
                                }
                            },
                        })}
                    >
                        <MultiselectItem
                            checked={optionsSelected.includes(opt.id)}
                            onChange={() => handleToggle(opt.id)}
                            className={opt.className}
                            active={activeIndex === idx}
                        >
                            {renderOption ? renderOption(opt) : opt.label}
                        </MultiselectItem>
                    </div>
                ))}
            </div>

            {enableSelectAll && (
                <div className="border-t bg-white border-gray-200 p-1.5 sticky bottom-0">
                    {emptyOption && (
                        <MultiselectItem
                            checked={optionsSelected.includes(emptyOption.id)}
                            onChange={() => handleToggle(emptyOption.id)}
                        >
                            {emptyOption.label}
                        </MultiselectItem>
                    )}

                    <MultiselectItem
                        checked={isAllSelected}
                        indeterminate={optionsSelectedWithoutEmpty.length > 0 && !isAllSelected}
                        onChange={() => {
                            onChangeSelected(isAllSelected ? [] : optionsFormatted.map((o) => o.id));
                        }}
                    >
                        {toggleAllText}
                    </MultiselectItem>
                </div>
            )}

            {actions.length > 0 && (
                <div
                    className="border-t bg-white border-gray-200 p-1.5 sticky bottom-0 gap-2"
                    onClick={() => setIsOpen(false)}
                >
                    <div className="flex flex-col gap-1">{actions}</div>
                </div>
            )}
        </>
    );
}

MultiselectContent.propTypes = {
    enableFilter: PropTypes.bool,
    query: PropTypes.string,
    onFilter: PropTypes.func,
    optionsFiltered: PropTypes.array,
    optionsSelected: PropTypes.array,
    handleToggle: PropTypes.func,
    renderOption: PropTypes.func,
    activeIndex: PropTypes.number,
    listRef: PropTypes.object,
    enableSelectAll: PropTypes.bool,
    emptyOption: PropTypes.object,
    isAllSelected: PropTypes.bool,
    optionsSelectedWithoutEmpty: PropTypes.array,
    onChangeSelected: PropTypes.func,
    optionsFormatted: PropTypes.array,
    toggleAllText: PropTypes.string,
    actions: PropTypes.array,
    setIsOpen: PropTypes.func,
    t: PropTypes.func,
};

export default Multiselect;

Multiselect.propTypes = {
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.node,
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            className: PropTypes.string,
        }),
    ),
    optionsSelected: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    onChangeSelected: PropTypes.func.isRequired,
    className: PropTypes.string,
    size: PropTypes.oneOf(['normal', 'small']),
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    floatLabel: PropTypes.string,
    disabled: PropTypes.bool,
    renderOption: PropTypes.func,
    toggleAllText: PropTypes.string,
    enableSelectAll: PropTypes.bool,
    enableClear: PropTypes.bool,
    onClear: PropTypes.func,
    enableFilter: PropTypes.bool,
    emptyValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    icon: PropTypes.node,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    highlight: PropTypes.bool,
    actions: PropTypes.arrayOf(PropTypes.node),
};
